import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { LngLatBounds, type Map as MapboxMap } from "mapbox-gl";
import type { PlaceGeoJSON } from "~/lib/api";
import {
  FIT_BOUNDS_PADDING,
  FLY_TO_OPTIONS,
  FLY_TO_ZOOM,
  MAP_MAX_ZOOM,
  type FitBoundsPadding,
} from "~/lib/mapConstants";
import { useBreakpoint } from "~/hooks/useBreakpoint";
import { ViewportContext, ViewportInternalsContext, type FlyToOptions } from "./context";

interface ViewportProviderProps {
  children: ReactNode;
}

/**
 * Provides viewport controls to children.
 * Canvas uses ViewportInternalsContext to register the map instance.
 */
export function ViewportProvider({ children }: ViewportProviderProps) {
  const mapRef = useRef<MapboxMap | null>(null);
  const [isReady, setIsReady] = useState(false);
  const { below } = useBreakpoint();
  const usesBottomSheet = below("lg");

  const flyTo = useCallback(
    (center: [number, number], options?: FlyToOptions) => {
      const map = mapRef.current;
      if (!map) return;

      // If fitWith has points, compute bounds from center + all points
      if (options?.fitWith && options.fitWith.length > 0) {
        const allPoints = [center, ...options.fitWith];
        const bounds = new LngLatBounds(allPoints[0], allPoints[0]);
        allPoints.slice(1).forEach((coord) => bounds.extend(coord));

        const defaultPadding = usesBottomSheet ? FIT_BOUNDS_PADDING.mobile : FIT_BOUNDS_PADDING.desktopWithPanel;
        map.fitBounds(bounds, {
          padding: options.padding ?? defaultPadding,
          ...FLY_TO_OPTIONS,
        });
        return;
      }

      // Standard flyTo behavior (no fitWith or empty fitWith)
      const targetZoom = options?.zoom ?? FLY_TO_ZOOM;
      const currentZoom = map.getZoom();
      const mapBounds = map.getBounds();
      const isVisible = mapBounds?.contains(center) ?? false;

      // Determine zoom behavior:
      // - Not visible: always zoom to target (need context for new area)
      // - Visible but zoomed out: zoom in to target (need more detail)
      // - Visible and zoomed in enough: no zoom change (reduce motion)
      const shouldZoom = !isVisible || currentZoom < targetZoom;

      map.flyTo({
        center,
        ...(shouldZoom && { zoom: targetZoom }),
        ...FLY_TO_OPTIONS,
      });
    },
    [usesBottomSheet],
  );

  const fitBounds = useCallback((geojson: PlaceGeoJSON, padding?: FitBoundsPadding) => {
    const map = mapRef.current;
    if (!map || geojson.features.length === 0) return;

    const validCoords = geojson.features
      .map((f) => f.geometry.coordinates)
      .filter(
        (c): c is [number, number] =>
          Array.isArray(c) && c.length === 2 && typeof c[0] === "number" && typeof c[1] === "number",
      );

    if (validCoords.length === 0) return;

    const bounds = new LngLatBounds(validCoords[0], validCoords[0]);
    validCoords.slice(1).forEach((coord) => bounds.extend(coord));

    map.fitBounds(bounds, {
      padding: padding ?? FIT_BOUNDS_PADDING.desktop,
      maxZoom: MAP_MAX_ZOOM,
      ...FLY_TO_OPTIONS,
    });
  }, []);

  const value = useMemo(() => ({ isReady, flyTo, fitBounds }), [isReady, flyTo, fitBounds]);
  const internals = useMemo(() => ({ mapRef, setIsReady }), []);

  return (
    <ViewportInternalsContext.Provider value={internals}>
      <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>
    </ViewportInternalsContext.Provider>
  );
}
